/**
 * E024: Database File Import - Shared Utilities
 *
 * Type detection and parsing utilities used by CSV, Excel, and SQL importers.
 */

import type { ColumnType } from './types';

export function columnName(index: number): string {
  const letters: string[] = [];
  let value = index;
  do {
    letters.unshift(String.fromCharCode(65 + (value % 26)));
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);

  return `Column ${letters.join('')}`;
}

/**
 * Create a rich-text value in BlockSuite format
 */
export function createRichTextValue(text: string): {
  '$blocksuite:internal:text$': true;
  delta: Array<{ insert: string }>;
} {
  return {
    '$blocksuite:internal:text$': true,
    delta: text.length === 0 ? [] : [{ insert: text }],
  };
}

/**
 * Detect column type from sample values
 */
export function detectColumnType(values: unknown[]): ColumnType {
  // Filter out null/undefined and take first 100 non-empty values
  const sample = values
    .filter(v => v != null && String(v).trim() !== '')
    .slice(0, 100)
    .map(v => String(v));

  if (sample.length === 0) return 'rich-text';

  // Check patterns in order of specificity
  if (sample.every(isCheckbox)) return 'checkbox';
  if (sample.every(isNumber)) return 'number';
  if (sample.every(isDate)) return 'date';
  if (sample.every(isUrl)) return 'link';

  return 'rich-text'; // default
}

/**
 * Check if value is a number
 */
export function isNumber(value: string): boolean {
  if (value.trim() === '') return false;
  const num = Number(value);
  return !isNaN(num) && isFinite(num);
}

/**
 * Check if value is a date
 */
export function isDate(value: string): boolean {
  // Common date patterns
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}$/, // 2024-01-15
    /^\d{2}\.\d{2}\.\d{4}$/, // 15.01.2024
    /^\d{2}\/\d{2}\/\d{4}$/, // 01/15/2024
    /^\d{4}\/\d{2}\/\d{2}$/, // 2024/01/15
  ];

  if (!datePatterns.some(pattern => pattern.test(value))) {
    return false;
  }

  const date = new Date(value);
  return !isNaN(date.getTime());
}

/**
 * Check if value is a checkbox (boolean)
 */
export function isCheckbox(value: string): boolean {
  const lower = value.toLowerCase().trim();
  return ['true', 'false', 'yes', 'no', '1', '0', 'x', ''].includes(lower);
}

/**
 * Check if value is a URL
 */
export function isUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Parse value according to detected type
 */
export function parseValue(value: string, type: ColumnType): unknown {
  if (value.trim() === '')
    return type === 'rich-text' ? createRichTextValue('') : null;

  switch (type) {
    case 'number':
      return Number(value);
    case 'date':
      return new Date(value).getTime();
    case 'checkbox': {
      const lower = value.toLowerCase().trim();
      return ['true', 'yes', '1', 'x'].includes(lower);
    }
    case 'link':
      return value;
    case 'rich-text':
      return createRichTextValue(value);
    default:
      return createRichTextValue(value);
  }
}
